"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";

interface UpcomingFeature {
  id: string;
  title: string;
  description: string;
  icon: string;
  tiers: string[];
  displayOrder: number;
  isActive: boolean;
}

const TIER_COLORS: Record<string, string> = {
  elite: "rgba(201,168,76,0.9)",
  premium: "rgba(180,150,100,0.8)",
  standard: "rgba(160,160,160,0.7)",
};

const getTierColor = (tier: string) => TIER_COLORS[tier.toLowerCase()] ?? "rgba(160,160,160,0.5)";

function IconByKey({ icon }: { icon: string }) {
  if (icon === "chat") return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
  if (icon === "ai") return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
    </svg>
  );
  if (icon === "sync") return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
    </svg>
  );
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export default function UpcomingFeatures() {
  const [features, setFeatures] = useState<UpcomingFeature[]>([]);
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    // Fetch upcoming features
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/upcoming-features`)
      .then((r) => r.json())
      .then((json) => setFeatures(json.data ?? []))
      .catch(() => {});

    // Fetch plans to make names dynamic
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/plans`)
      .then((r) => r.json())
      .then((json) => setPlans(json.data ?? []))
      .catch(() => {});
  }, []);

  // Sort plans by price to identify tiers
  const activePlans = plans.filter(p => p.isActive && p.isPublic);
  const sortedPlans = [...activePlans].sort((a, b) => 
    (Number(b.priceMonthly) || 0) - (Number(a.priceMonthly) || 0)
  );

  const highTierNames = sortedPlans.slice(0, 2).map(p => p.name);
  const dynamicPlanText = highTierNames.length > 0 
    ? highTierNames.join(" & ") 
    : "Premium & Elite";

  const getDynamicTierColor = (tierName: string) => {
    const planIndex = sortedPlans.findIndex(p => p.name.toLowerCase() === tierName.toLowerCase());
    if (planIndex === 0) return TIER_COLORS.elite;
    if (planIndex === 1) return TIER_COLORS.premium;
    return TIER_COLORS.standard;
  };

  if (features.length === 0) return null;

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.15 } },
  };

  const item = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55 } },
  };

  return (
    <section
      className="py-24 px-6 scroll-mt-24"
      aria-label="Upcoming features"
      style={{ scrollMarginTop: "6rem" }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <span
            className="inline-block text-xs tracking-[0.25em] uppercase mb-4"
            style={{ color: "rgba(201,168,76,0.8)", fontFamily: "'Montserrat', sans-serif" }}
          >
            What&apos;s Coming
          </span>
          <h2
            className="text-4xl md:text-5xl font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(255,255,255,0.92)" }}
          >
            Future Capabilities
          </h2>
          <p
            className="mt-4 text-sm max-w-xl mx-auto"
            style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Montserrat', sans-serif" }}
          >
            These high-value features are in active development — available first to {dynamicPlanText} members.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {features.map((feat) => (
            <motion.div
              key={feat.id}
              variants={item}
              whileHover={{ y: -5, transition: { duration: 0.25 } }}
              className="relative rounded-2xl p-6 overflow-hidden"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(201,168,76,0.12)",
                backdropFilter: "blur(12px)",
                transition: "box-shadow 0.3s ease, border 0.3s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.boxShadow = "0 0 28px 0 rgba(201,168,76,0.08)";
                el.style.border = "1px solid rgba(201,168,76,0.28)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.boxShadow = "none";
                el.style.border = "1px solid rgba(201,168,76,0.12)";
              }}
            >
              {/* Coming Soon ribbon */}
              <div
                className="absolute top-4 right-[-28px] rotate-45 text-[10px] tracking-widest uppercase px-8 py-0.5"
                style={{
                  background: "rgba(201,168,76,0.18)",
                  color: "rgba(201,168,76,0.9)",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Soon
              </div>

              <div
                className="mb-5 w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(201,168,76,0.07)", color: "rgba(201,168,76,0.85)" }}
              >
                <IconByKey icon={feat.icon} />
              </div>

              <h3
                className="text-lg font-light mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(255,255,255,0.88)" }}
              >
                {feat.title}
              </h3>

              <p
                className="text-sm leading-relaxed mb-5"
                style={{ color: "rgba(255,255,255,0.42)", fontFamily: "'Montserrat', sans-serif" }}
              >
                {feat.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {(feat.tiers ?? []).map((tier) => (
                  <span
                    key={tier}
                    className="text-[10px] tracking-widest uppercase px-3 py-1 rounded-full"
                    style={{
                      border: `1px solid ${getDynamicTierColor(tier)}`,
                      color: getDynamicTierColor(tier),
                      fontFamily: "'Montserrat', sans-serif",
                      background: "rgba(255,255,255,0.02)",
                    }}
                  >
                    {tier}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
